// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect, useRef } from "react";
import { useChatSocket } from "@/hooks/useChatSocket";
import useChatStore from "@/stores/chatStore";
import ChatDialog from "@/components/chat/ChatDialog";
import ChatBubbleNotification from "@/components/chat/ChatBubbleNotification";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { contactAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  GraduationCap,
  LayoutDashboard,
  BookOpen,
  Settings,
  LogOut,
  User,
  Menu,
  Shield,
  Sparkles,
  MessageSquare,
  MessageCircle,
  ChevronDown,
  ChevronRight,
  Activity,
  Users,
  UserCheck,
  UsersRound,
  Lock,
  Key,
  FlaskConical,
  GitBranch,
  Mail,
  HardDrive,
  CalendarClock,
  FileText,
  Wifi,
  Video,
  Store,
  RefreshCw,
  Clapperboard,
  BookMarked,
} from "lucide-react";
import { getInitials } from "@/lib/utils";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { adminAPI } from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin sub-menu items
const ADMIN_SUBITEMS = [
  { tab: "overview", label: "Vue d'ensemble", icon: Activity, trainerAllowed: true },
  { tab: "users", label: "Utilisateurs", icon: Users },
  { tab: "approvals", label: "Approbations", icon: UserCheck, trainerAllowed: true },
  { tab: "groups", label: "Groupes", icon: UsersRound, trainerAllowed: true },
  { tab: "locked-accounts", label: "Comptes bloqués", icon: Lock },
  { tab: "contacts", label: "Contact", icon: MessageSquare },
  { tab: "permissions", label: "Permissions", icon: Key, adminOnly: true },
  { tab: "labs", label: "Labs", icon: FlaskConical, adminOnly: true },
  { tab: "config", label: "Configuration", icon: Settings, adminOnly: true },
  { tab: "llm-config", label: "Config IA", icon: Sparkles, adminOnly: true },
  { tab: "git-connections", label: "Connexions Git", icon: GitBranch, adminOnly: true },
  { tab: "email-templates", label: "Templates Email", icon: Mail, adminOnly: true },
  { tab: "backup", label: "Backup & Restore", icon: HardDrive, permissionField: "can_access_backup" },
  { tab: "temp-sessions", label: "Sessions Temp.", icon: CalendarClock, permissionField: "can_access_temp_sessions" },
  { tab: "documentation", label: "Documentation", icon: FileText, adminOnly: true },
  { tab: "security", label: "Sécurité", icon: Shield, adminOnly: true },
  { tab: "webinars", label: "Webinaires Live", icon: Video, href: "/admin/webinars", trainerAllowed: true },
  { tab: "webinar-sessions", label: "Sessions Webinaire", icon: Wifi, href: "/admin/webinar-sessions", trainerAllowed: true },
  { tab: "vitrine", label: "Vitrine", icon: Store, href: "/admin/vitrine", adminOnly: true },
  { tab: "tutorials", label: "Tutoriels", icon: BookMarked, adminOnly: true },
];

const ROLE_CONFIG = {
  admin: { label: "Administrateurs", icon: Shield, color: "#8b5cf6" },
  trainer: { label: "Formateurs", icon: GraduationCap, color: "#3b82f6" },
  candidate: { label: "Candidats", icon: UserCheck, color: "#22c55e" },
};
const ROLE_ORDER = ["admin", "trainer", "candidate"];

const getTimeSince = (isoStr) => {
  if (!isoStr) return "";
  try {
    const dt = new Date(isoStr);
    const mins = Math.floor((Date.now() - dt) / 60000);
    if (mins < 1) return "à l'instant";
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h`;
  } catch {
    return "";
  }
};

export const AppLayout = ({ children, workspaceId = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, isAdmin, isManager, isTrainer, permissions } = useAuth();
  const [adminExpanded, setAdminExpanded] = useState(location.pathname === "/admin");

  // Direct Chat integration
  const { emitTyping } = useChatSocket(user);
  const { openChat, totalUnread } = useChatStore();

  const handleUserChatClick = (targetUser) => {
    // Anyone can open a chat (formateur initiates, candidat responds)
    openChat(targetUser);
  };

  // Online users state
  const [onlineData, setOnlineData] = useState(null);
  const [onlineCollapsed, setOnlineCollapsed] = useState(false);
  const onlineInterval = useRef(null);
  const token = localStorage.getItem("token");

  // Feature flags: Mon Dossier visibility
  const [monDossierEnabled, setMonDossierEnabled] = useState(true);
  const [contactBadge, setContactBadge] = useState(0);

  useEffect(() => {
    const fetchBadge = async () => {
      try {
        const res = await contactAPI.getBadgeCounts();
        setContactBadge(res.data?.contacts || 0);
      } catch(e) {}
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadFeatureFlags = async () => {
      try {
        const res = await adminAPI.getFeatureFlags();
        if (res.data) {
          setMonDossierEnabled(res.data.mon_dossier_enabled !== false);
        }
      } catch (err) {
        // Default to true if endpoint fails
        console.error("Feature flags load error:", err);
      }
    };
    if (token) loadFeatureFlags();
  }, [token]);

  // Collapse online panel when admin is expanded
  useEffect(() => {
    if (adminExpanded) {
      setOnlineCollapsed(true);
    }
  }, [adminExpanded]);

  // Fetch online users (admin only)
  const fetchOnline = async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${API_URL}/api/users/online`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setOnlineData(await res.json());
    } catch (err) {
      console.error("Online fetch error:", err);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchOnline();
    onlineInterval.current = setInterval(fetchOnline, 30000);
    return () => clearInterval(onlineInterval.current);
  }, [isAdmin]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isOnAdmin = location.pathname === "/admin";
  const currentAdminTab = searchParams.get("tab") || "overview";

  const isCandidate = user?.role === "candidate";

  const mainNavItems = [
    { href: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard },
    { href: "/contact", label: "Contact", icon: MessageSquare, badge: contactBadge || null, badgeColor: "bg-red-500" },
    { href: "/mon-dossier", label: "Mon dossier", icon: FileText, candidateOnly: true },
    { href: "/documentation", label: "Documentation", icon: BookOpen },
  ].filter(item => {
    // Hide "Mon dossier" from candidates if the admin has disabled it
    if (item.candidateOnly && isCandidate && !monDossierEnabled) return false;
    return true;
  });

  const NavContent = () => (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex flex-col border-b border-border/50">
        <div className="flex h-16 items-center gap-3 px-6">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/20">
            <GraduationCap className="h-5 w-5 text-white" />
          </div>
          <div>
            <span className="text-lg font-bold gradient-text-primary">DevOpsLearn</span>
            <p className="text-[10px] text-muted-foreground -mt-0.5 tracking-wider uppercase">DevOps Learning Platform</p>
          </div>
        </div>
        <div className="px-6 pb-4">
          <div className="bg-[#0B1120] p-2 rounded-xl shadow-md w-full h-16 flex items-center justify-center ring-1 ring-border/50">
            <img src="/logo.png" alt="DevOpsLearn" className="h-full w-auto object-contain" />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 stagger-children">
        {mainNavItems.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          return (
            <Link
              key={item.href}
              to={item.href}
              className={`sidebar-item ${isActive ? "active" : ""}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <item.icon className="h-5 w-5" />
              <span className="flex-1">{item.label}</span>
              {item.badge && (
                <span className={"ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full " + (item.badgeColor || "bg-primary") + " text-white min-w-[18px] text-center"}>
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}

        {/* Staff Chat Link */}
        {(isAdmin || isManager || isTrainer) && (
          <Link
            to="/staff-chat"
            className={`sidebar-item ${location.pathname === "/staff-chat" ? "active" : ""}`}
            data-testid="nav-staff-chat"
          >
            <MessageSquare className="h-5 w-5" />
            <span className="flex-1">Staff Chat</span>
          </Link>
        )}

        {/* Recording Studio Link — admins only */}
        {isAdmin && (
          <Link
            to="/studio"
            className={`sidebar-item ${location.pathname.startsWith("/studio") ? "active" : ""}`}
            data-testid="nav-recording-studio"
          >
            <Clapperboard className="h-5 w-5" />
            <span className="flex-1">Recording Studio</span>
            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: "linear-gradient(135deg, #ef4444, #f97316)" }}>
              PRO
            </span>
          </Link>
        )}

        {/* Administration collapsible tree */}
        {(isAdmin || isManager || isTrainer) && (
          <div className="mt-1">
            {/* Parent: Administration */}
            <button
              onClick={() => {
                setAdminExpanded(!adminExpanded);
                if (!isOnAdmin) navigate("/admin");
              }}
              className={`sidebar-item w-full ${isOnAdmin ? "active" : ""}`}
              data-testid="nav-administration"
            >
              <Shield className="h-5 w-5" />
              <span className="flex-1 text-left">Administration</span>
              {adminExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform" />
              )}
            </button>

            {/* Sub-items */}
            <div
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                maxHeight: adminExpanded ? `${"1500"}px` : "0px",
                opacity: adminExpanded ? 1 : 0,
              }}
            >
              <div className="ml-3 pl-3 border-l-2 border-border/40 space-y-0.5 mt-1">
                {ADMIN_SUBITEMS
                  .filter(item => {
                    if (item.adminOnly) return isAdmin;
                    if (isAdmin) return true;
                    if (item.permissionField) return !!permissions?.[item.permissionField];
                    if (isTrainer && item.trainerAllowed) return true;
                    if (isTrainer) return false; // trainer only sees trainerAllowed items
                    return true; // manager sees non-adminOnly items
                  })
                  .map((item) => {
                    const isSubActive = item.href ? location.pathname.startsWith(item.href) : (isOnAdmin && currentAdminTab === item.tab);
                    const targetTo = item.href ? item.href : `/admin?tab=${item.tab}`;
                    return (
                      <Link
                        key={item.tab}
                        to={targetTo}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-all duration-150 ${
                          isSubActive
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        }`}
                      >
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </Link>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Online Users Panel (admin only) */}
      {isAdmin && (
        <div className="border-t border-border/50">
          {/* Toggle header */}
          <button
            onClick={() => setOnlineCollapsed(!onlineCollapsed)}
            className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <div className="relative">
                <Users className="h-4 w-4 text-primary" />
                <span className="absolute -top-1 -right-1.5 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              </div>
              <span className="text-xs font-semibold">Utilisateurs en ligne</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
                style={{ background: "linear-gradient(135deg, #8b5cf6, #3b82f6)" }}
              >
                {onlineData?.total || 0}
              </span>
              {onlineCollapsed ? (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </div>
          </button>

          {/* Expandable content */}
          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              maxHeight: onlineCollapsed ? "0px" : "300px",
              opacity: onlineCollapsed ? 0 : 1,
            }}
          >
            <div className="px-3 pb-3 space-y-2 max-h-[280px] overflow-y-auto scrollbar-thin">
              {ROLE_ORDER.map((role) => {
                const config = ROLE_CONFIG[role];
                const users = onlineData?.by_role?.[role] || [];
                if (users.length === 0) return null;
                const Icon = config.icon;

                return (
                  <div key={role}>
                    <div className="flex items-center gap-1.5 px-1 py-1">
                      <Icon className="h-3 w-3" style={{ color: config.color }} />
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: config.color }}
                      >
                        {config.label}
                      </span>
                      <span
                        className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                        style={{ background: `${config.color}15`, color: config.color }}
                      >
                        {users.length}
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      {users.map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-primary/10 transition-colors cursor-pointer group" onClick={() => u.id !== user?.id && handleUserChatClick(u)} title={u.id !== user?.id ? "Envoyer un message" : ""}
                        >
                          <div className="relative flex-shrink-0">
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={u.avatar} />
                              <AvatarFallback
                                className="text-[9px] font-bold text-white"
                                style={{ background: config.color }}
                              >
                                {getInitials(u.first_name, u.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 border border-background" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium truncate leading-tight">
                              {u.first_name} {u.last_name}
                            </p>
                          </div>
                          {u.id !== user?.id ? (
                            <MessageCircle className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          ) : (
                            <span className="text-[9px] text-muted-foreground flex-shrink-0">
                              {getTimeSince(u.last_activity_at)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {onlineData && onlineData.total === 0 && (
                <div className="text-center py-4">
                  <p className="text-[11px] text-muted-foreground">Aucun en ligne</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User section */}
      <div className="border-t border-border/50 p-4">
        <div className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-primary/5 transition-all duration-200 cursor-pointer">
          <div className="relative">
            <Avatar className="h-9 w-9 ring-2 ring-primary/20">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-sm font-bold">
                {getInitials(user?.first_name, user?.last_name)}
              </AvatarFallback>
            </Avatar>
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white" />
          </div>
          <div className="flex-1 truncate">
            <p className="text-sm font-semibold truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
          </div>
        </div>
      </div>

      {/* Author badge */}
      <div className="px-4 py-3 text-center border-t border-border/30">
        <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-muted-foreground/60">
          KHLIFI HOUCEM
        </p>
        <p className="text-[8px] tracking-wider uppercase mt-0.5 text-muted-foreground/40">
          Cloud && DevSecOps
        </p>
      </div>
    </div>
  );

  return (
    <div className="app-shell">
      {/* Desktop Sidebar */}
      <aside className="app-sidebar">
        <NavContent />
      </aside>

      {/* Main Content */}
      <div className="app-main">
        {/* Top Header */}
        <header className="glass sticky top-0 z-40 flex h-14 items-center justify-between px-4 md:px-6">
          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <NavContent />
            </SheetContent>
          </Sheet>

          <div className="flex-1" />

          {/* Signature */}
          <div className="hidden md:flex items-center mr-2">
            <span className="text-xs font-semibold text-primary/80 tracking-wide border border-primary/30 rounded-full px-3 py-1 bg-primary/5">
              KHLIFI HOUCEM &nbsp;|&nbsp; FORMATEUR CLOUD &amp;&amp; DEVSECOPS
            </span>
          </div>

          {/* Notifications */}
          <NotificationBell />

          {/* User Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 rounded-xl hover:bg-primary/5" data-testid="user-menu-trigger">
                <Avatar className="h-7 w-7">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-[10px] font-bold">
                    {getInitials(user?.first_name, user?.last_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline-block text-sm font-medium">{user?.first_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl shadow-xl border-border/50">
              <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profile")} className="rounded-lg cursor-pointer" data-testid="menu-profile">
                <User className="mr-2 h-4 w-4" />
                Profil
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate("/admin")} className="rounded-lg cursor-pointer" data-testid="menu-admin">
                  <Shield className="mr-2 h-4 w-4" />
                  Administration
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="rounded-lg cursor-pointer text-rose-500 focus:text-rose-500" data-testid="menu-logout">
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main>{children}</main>

        {/* Direct Chat Dialog */}
        <ChatDialog emitTyping={emitTyping} />
        <ChatBubbleNotification />
      </div>
    </div>
  );
};
